<template>
  <validation-observer
    v-slot="observer"
    slim
  >
    <validation-observer-event-emitter
      :observer="observer"
      @update="onObserverUpdate"
    >
      <div
        v-for="(data, index) in parsedData"
        :key="index"
        class="edit-wrapper"
      >
        <!--   SUMMARY FUNCTION  -->
        <div
          v-if="formsData.length && !data.isOpen"
          :class="['summary-section', 'container',{'pb-10': index === formsData.length-1 && isEditProcess}]"
        >
          <h3 class="text-capitalize mb-2 text-h5 font-weight-bold">{{ formLabel }} {{ index + 1 }}</h3>
          <validation-provider
            :vid="`${stepId}_summary-validity`"
            :rules="{ is: { other: true } }"
            name="Summary Validity"
            immediate
          >
            <input
              type="hidden"
              :value="itemsValidity[index]"
            >
            <box
              :id="`item-${index}`"
              :is-edit-btn-shown="false"
            >
              <slot
                name="preview"
                :data="{ data }"
                :index="{ index }"
              />
              <a
                v-if="formsData.length > 1"
                slot="left-action"
                href="#"
                class="link-box"
                @click.prevent="removeForm(index)"
              >
                <span>
                  Remove
                </span>
                <v-icon
                  color="primary"
                  right
                >
                  mdi-delete
                </v-icon>
              </a>
              <a
                v-if="!isEditProcess && !showNewForm"
                slot="right-action"
                href="#"
                class="link-box"
                @click.prevent="openEditForm(index)"
              >
                <span>Change these details</span>
                <v-icon color="primary">mdi-pencil</v-icon>
              </a>
            </box>
          </validation-provider>
        </div>
        <!--   SUMMARY FUNCTION  -->

        <!--  EDIT FORM  -->
        <div
          v-if="data.isOpen"
          :id="`edit-form-${index}`"
        >
          <form-section
            no-validity
            class="forms-wrapper"
          >
            <component
              :is="question.questionComponent"
              v-for="question in questions"
              :key="question.questionId || question.questionComponent"
              :question-id="`${question.questionId}`"
              v-bind="question.params || {}"
              :form-observer="observer"
              :form-data="formsData[index]"
              :step-data="editFormData"
              :step-id="stepId"
              :product-name="productName"
              @input="handleEditForm"
            />

            <multiform-controls
              v-if="!isProductPet || (isProductPet && observer.valid)"
              class="container pb-10"
              :product-label="formLabel"
              :is-cancel-btn-shown="isEditProcess"
              :is-edit="isEditProcess"
              @save="editForm(index,observer)"
              @clear="cancelEdit(index)"
            />
          </form-section>
        </div>
        <!--  EDIT FORM  -->
      </div>

      <!--  NEW FORM  -->
      <div
        v-if="!formsData.length || showNewForm"
        id="new-form"
      >
        <form-section
          no-validity
          class="forms-wrapper"
        >
          <div
            v-if="!!formsData.length"
            class="container d-flex justify-space-between align-center"
          >
            <h3 class="text-capitalize text-h5 font-weight-bold">{{ formLabel }} {{ formsData.length + 1 }}</h3>
            <multiform-controls
              :is-save-btn-shown="false"
              :is-cancel-btn-shown="!!formsData.length"
              @clear="resetSave"
            />
          </div>

          <component
            :is="question.questionComponent"
            v-for="question in questions"
            :key="question.questionId || question.questionComponent"
            :question-id="question.questionId"
            v-bind="question.params || {}"
            :form-observer="observer"
            :form-data="newFormData"
            :step-data="newFormData"
            :step-id="stepId"
            :product-name="productName"
            @input="handleNewForm"
          />

          <div class="container">
            <slot
              v-if="!isProductPet || (isProductPet && observer.valid)"
              name="info"
            />
          </div>

          <multiform-controls
            class="container pb-10"
            :product-label="formLabel"
            :is-save-btn-shown="!isProductPet || (isProductPet && observer.valid)"
            :is-cancel-btn-shown="!!formsData.length"
            @save="saveNewForm(observer)"
            @clear="resetSave"
          />
        </form-section>
      </div>
      <!--  NEW FORM  -->

      <div
        v-if="formsData.length && !showNewForm && !isEditProcess"
        class="container mt-10 pb-10"
      >
        <slot
          v-if="!isProductPet || (isProductPet && !observer.failed)"
          name="info"
        />
        <multiform-controls
          v-if="formsData.length < maxAmount"
          :is-save-btn-shown="!isProductPet || (isProductPet && !observer.failed)"
          :product-label="formLabel"
          @save="openNewForm"
        />
        <p v-else>You can add up to {{ maxAmount }} <span class="text-lowercase">{{ productTitle }}s.</span></p>
      </div>
    </validation-observer-event-emitter>
  </validation-observer>
</template>

<script>
import { isEqual } from 'lodash';
import { mapActions } from 'vuex';
import MultiformControls from '@/components/MultiformController/MultiformControls';

export default {
  name: 'multiform-controller',
  components: {
    MultiformControls,
  },
  props: {
    questions: {
      type: Array,
      required: true,
    },
    productName: {
      type: String,
      required: true,
    },
    productTitle: {
      type: String,
      required: true,
    },
    formsData: {
      type: Array,
      default: () => [],
    },
    initialData: {
      type: Object,
      required: true,
    },
    maxAmount: {
      type: Number,
      required: true,
    },
    stepId: {
      type: String,
      required: true,
    },
    formLabel: {
      type: String,
      required: true,
    },
    isContinueTriggered: {
      type: Boolean,
      required: true,
    },
    itemsValidity: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      showNewForm: false,
      isEditProcess: false,
      newFormData: this.initialData,
      editFormData: {},
      formObserver: null,
    };
  },
  computed: {
    parsedData () {
      return this.formsData.map(data => ({ ...data, isOpen: false }));
    },
    isProductPet () {
      return this.productName === 'pet';
    },
  },
  watch: {
    async isContinueTriggered (val) {
      // watch value from useQuotezoneForm, if 'continue next step' is pressed and some form is still opened
      // check if it is a new form or edit mode, if form is valid save data and allow user go to next step
      if (!val) return;

      if (this.showNewForm) {
        await this.saveNewForm(this.formObserver);
        return;
      }

      this.parsedData.forEach((data, index) => data.isOpen && this.editForm(index, this.formObserver));
    },
    formsData: {
      handler (val, preVal) {
        if (!val.length) {
          this.showNewForm = true;
          this.isEditProcess = true;
        }

        if (!!preVal && !isEqual(val, preVal)) {
          this.$emit('multiform-changed', true);
        }
      },
      immediate: true,
      deep: true,
    },
    showNewForm: {
      handler () {
        this.emitMultiformStatus();
      },
      immediate: true,
    },
    isEditProcess: {
      handler (val) {
        this.emitMultiformStatus();
        this.$emit('edit-process', val);

        if (!val) {
          this.$emit('reset-continue-trigger', false);
        }
      },
      immediate: true,
    },
    itemsValidity: {
      handler (val, prevVal) {
        // FIX for PU-5106 - open edit form for single pet with validation errors
        // Check if data is not full or has validation errors only for the first item(pet)
        if (this.isProductPet && val.length === 1 && !val[0] && prevVal.length === 0) {
          this.checkIfDataNotFull();
        }
      },
    },
  },
  beforeDestroy () {
    this.$emit('multiform-changed', false);
    this.$emit('edit-process', false);
  },
  created () {
    this.checkIfDataNotFull();
  },
  methods: {
    ...mapActions(['showConfirmModal']),
    handleEditForm ($event) {
      this.editFormData = { ...this.editFormData, ...$event };
      this.$emit('input', this.editFormData);
    },
    handleNewForm ($event) {
      this.newFormData = { ...this.newFormData, ...$event };
      this.$emit('input', this.newFormData);
    },
    emitMultiformStatus () {
      this.$emit('status', {
        isEditProcess: this.isEditProcess,
        showNewForm: this.showNewForm,
      });
    },
    /* TODO:refactor - this data should be contained in parent and passed as a prop
    rather then collected in child and then passed back to parent */
    onObserverUpdate (observer) {
      this.formObserver = observer;
      // Emit this data for using in error section
      if (!Object.keys(observer.fields).length) return;

      const shortQuestionsDetails = [];

      this.questions.forEach((question) => {
        for (const item in observer.fields) {
          const fieldName = observer.fields[item].name || item.split(`${this.stepId}_`).join('');
          if (question.questionId === fieldName) {
            shortQuestionsDetails.push(
              {
                questionId: question.questionId,
                title: question.params.title,
                field: observer.fields[item].id,
                // To order based on the original question order
                index: this.questions.indexOf(question),
              },
            );
          }
        }
      });

      this.$emit('multiform-questions-details', shortQuestionsDetails);
    },
    removeForm (index) {
      const items = this.formsData.filter((item, i) => i !== index);

      this.showConfirmModal({
        noButton: 'Cancel',
        yesButton: 'Ok',
        yesCallback: () => {
          this.$emit('set-data', items);
          this.isEditProcess = false;
        },
      });
    },
    async openEditForm (index) {
      this.isEditProcess = true;
      this.parsedData[index].isOpen = true;
      this.editFormData = this.formsData[index];

      await this.$nextTick();
      this.$scrollTo(`#edit-form-${index}`);
      this.$emit('input', this.editFormData);
    },
    async saveNewForm (observer) {
      const isValid = await observer.validate();

      // check if form is invalid show errors, otherwise save data and open new form
      if (!isValid) {
        const failedField = Object.values(observer.fields).find(field => field.invalid);
        this.$scrollTo(`#${failedField.name}`, 300);
      } else {
        const { errors, ...data } = this.newFormData; // remove errors field from the form data after validation
        this.$emit('set-data', [...this.formsData, data]);
        this.showNewForm = false;
        this.isEditProcess = false;

        if (!this.isContinueTriggered) {
          // if current form is valid and saved and already added less than maxAmount, it is automatically opened new form
          await this.$nextTick();
          if (this.formsData.length < this.maxAmount) {
            await this.openNewForm();
          }
        }
      }
    },
    resetSave () {
      this.$emit('input', {});
      this.showNewForm = false;
      this.isEditProcess = false;
    },
    async editForm (index, observer) {
      const isValid = await observer.validate();

      if (!isValid) {
        const failedField = Object.values(observer.fields).find(field => field.invalid);
        this.$scrollTo(`#${failedField.name}`, 300);
      } else {
        const items = this.formsData.map(a => ({ ...a }));
        const { errors, ...data } = this.editFormData; // remove errors field from the form data after validation
        items[index] = data;
        this.$emit('set-data', items);
        this.isEditProcess = false;
      }
    },
    cancelEdit (index) {
      this.$emit('show-form-actions');
      this.editFormData = {};
      this.parsedData[index].isOpen = false;
      this.isEditProcess = false;
    },
    async openNewForm () {
      this.newFormData = this.initialData;
      this.showNewForm = true;
      this.isEditProcess = true;
      await this.$nextTick();
      this.$scrollTo('#new-form');
    },
    checkIfDataNotFull () {
      // Force add new form with prepopulated data if we have only one item and it has missing fields
      if (this.parsedData.length === 1) {
        const dataNotFull = Object.keys(this.initialData).some(key => this.formsData[0][key] === undefined);
        const dataNotValid = this.itemsValidity.some(item => item === false);

        if (dataNotFull || dataNotValid) {
          this.showNewForm = true;
          this.newFormData = { ...this.parsedData[0] };
          this.$emit('set-data', []);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$padding-top: _percentage(50px, 934px);
$padding_side: _percentage(100px, 934px);

.edit-wrapper {
  & + .edit-wrapper {
    .summary-section {
      margin-top: 25px;
    }
  }

  &:first-child {
    .summary-section {
      padding-top: calc(#{$padding-top});
    }
  }
}

.summary-section {
  &::v-deep {
    .box__actions {
      margin-left: 72px;
    }
  }
}

.container {
  padding: 0 $padding_side 1px;

  @media screen and (max-width: 767px) {
    padding: 0 $padding_side / 2 1px;
  }
}

.forms-wrapper {
  &::v-deep {
    & > .form-section__container {
      padding: 0;

      & > .form-section__container-holder {
        padding: 0;
      }
    }

    &.is-failed {
      .help-text__bg {
        fill: var(--v-error-base, $clr-error);
      }
    }
  }
}
</style>
